import { log } from '@updater-core/interactors/move-items/logger';
import { ITEM_KINDS, ITEM_STATUSES } from '@updater-core/constants/items';
import { DomainTracker } from '@updater-core/interactors/domain-tracker';
import { SessionInteractor } from '@updater-core/interactors/shell/session/interactor.d';
import { Store } from '@updater-core/lib/store/types';
import { MoveItemsGateway } from '@updater-core/gateways/move-items';
import { Declined, MoveItemsState } from './types';
import * as selectors from './selectors';

export function fetchRemoteItems({
  store,
  moveItemsGateway,
}: {
  store: Store<MoveItemsState>;
  moveItemsGateway: MoveItemsGateway;
}) {
  return async () => {
    const items = await moveItemsGateway.items();
    store.setState({
      items,
    });
    return items;
  };
}

export function fetchDeclinedItems({
  store,
  session,
}: {
  store: Store<MoveItemsState>;
  session: SessionInteractor;
}) {
  return async (): Promise<Declined> => {
    await session.actions.fetchSession();
    const declinedItems = session.selectors.declinedItems(session.getState());
    store.setState({
      declinedItems,
    });
    return declinedItems;
  };
}

export function setStepStatus({
  store,
  stepTracker,
  moveItemsGateway,
  session,
}: {
  store: Store<MoveItemsState>;
  stepTracker: DomainTracker;
  moveItemsGateway: MoveItemsGateway;
  session: SessionInteractor;
}) {
  return async (kind: ITEM_KINDS, status: ITEM_STATUSES, source?: string) => {
    // Assume declined item state does not need to be refetched
    // Session call is expensive, so only do it if we need to
    let { declinedItems } = store.getState();
    // TODO: Determine if we can safely assume we do not need to grab current items
    // For now, fetch remote items before updating status to guarentee latest
    let remoteItems = await moveItemsGateway.items();
    // Get merged items to get current status from both items and declines
    const mergedItems = selectors.mergedItems({
      items: remoteItems,
      declinedItems,
    });

    log(`Will set status of item with kind ${kind} to ${status}.`);

    const targetItem = mergedItems[kind];

    function trackStatusUpdate() {
      stepTracker.actions.trackUpdate('status', {
        source,
        previousStatus: targetItem?.status,
        newStatus: status,
        kind,
      });
    }

    function throwIfNonSingleton() {
      if (kind === ITEM_KINDS.FORWARD_MAIL) {
        throw new Error(
          `${kind} cannot be updated to ${status} because it is not a singleton item kind`
        );
      }
    }

    switch (status) {
      case ITEM_STATUSES.STARTED:
        throwIfNonSingleton();
        if (!targetItem?.status) {
          await moveItemsGateway.startItem(kind);
          trackStatusUpdate();
          log(`Did set status of item with kind ${kind} to ${status}.`);
        } else {
          log(
            `Tried to set status to started but ${kind} item already has a status ${targetItem?.status}.`
          );
        }
        break;
      case ITEM_STATUSES.RESERVED:
        throwIfNonSingleton();
        if (targetItem?.id) {
          await moveItemsGateway.reserveItem(targetItem.id);
          trackStatusUpdate();
          log(`Did set status of item with kind ${kind} to ${status}.`);
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            `Couldn't set ${kind} status to completed because item no item of that kind exists yet.`
          );
        }
        break;
      case ITEM_STATUSES.COMPLETED:
        throwIfNonSingleton();
        if (targetItem?.id) {
          await moveItemsGateway.completeItem(targetItem.id);
          trackStatusUpdate();
          log(`Did set status of item with kind ${kind} to ${status}.`);
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            `Couldn't set ${kind} status to completed because item no item of that kind exists yet.`
          );
        }
        break;
      case ITEM_STATUSES.ALREADY_DONE:
      case ITEM_STATUSES.NOT_NEEDED:
        await moveItemsGateway.markItemStatusOnMove(kind, status);
        trackStatusUpdate();
        declinedItems = await fetchDeclinedItems({ store, session })();
        log(`Did set status of item with kind ${kind} to ${status}.`);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn(`Don't know how to update ${kind} to ${status}.`);
    }

    // TODO: We must always refetch remote items since marking some items as "already done" sets completed
    // Observed this "already_done" => "completed" behavior with insurance item kind
    remoteItems = await moveItemsGateway.items();

    store.setState({
      items: remoteItems,
      declinedItems,
    });
  };
}

export function setItemMetadata({
  store,
  moveItemsGateway,
}: {
  store: Store<MoveItemsState>;
  moveItemsGateway: MoveItemsGateway;
}) {
  return async (kind: ITEM_KINDS, metadata: any) => {
    const item = await moveItemsGateway.setItemMetadata({ kind, metadata });

    const { items } = store.getState();

    store.setState({
      items: [...items.filter(({ kind: itemKind }) => itemKind !== kind), item],
    });
  };
}
